import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, RotateCw, Upload } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { applicationSourceLabel, type ImportRowFilter, type ImportRowView, type ImportView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Segmented } from '../../../components/Segmented';
import { SkeletonLines } from '../../../components/Skeleton';
import { ToneChip } from '../../../components/Tone';
import { documentUrl } from '../../../components/cv/pdf-cache';
import { formatDateTime, formatNumber } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { cancelImport, importKey, IMPORTS_KEY, retryImportRow, uploadImportRowCv, useImportRows } from '../api/imports-api';
import { IMPORT_STATUS_LABELS, IMPORT_STATUS_TONES } from './import-status';
import styles from './imports.module.css';

// IMP-02 (§L29, F22.05–F22.08): the summary line exactly as §L29 words it, the progress of the
// rows and the CVs, and a table of what needs a person, each row with Retry and Upload CV. Live
// through the job room's realtime hint (`['imports']`), never polled. N-86 opens here.

const CV_ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const FILTER_OPTIONS: readonly { readonly value: ImportRowFilter; readonly label: string }[] = [
  { value: 'needs_you', label: 'Needs you' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'all', label: 'All rows' },
];

function percent(part: number, whole: number): number {
  return whole === 0 ? 0 : Math.min(100, Math.round((part / whole) * 100));
}

export function ImportProgress({ view }: { readonly view: ImportView }): ReactNode {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const show = useToasts((toasts) => toasts.show);
  const [isCancelling, setIsCancelling] = useState(false);
  const { counts } = view;
  const imported = counts.created + counts.linked;
  const cvsSettled = imported - counts.cvQueued;

  const cancel = async (): Promise<void> => {
    setIsCancelling(true);
    try {
      queryClient.setQueryData(importKey(view.id), await cancelImport(view.id, view.version));
      await queryClient.invalidateQueries({ queryKey: [...IMPORTS_KEY, 'list'] });
    } catch (error) {
      show({ tone: 'danger', message: "Couldn't cancel the import", detail: error instanceof Error ? error.message : 'Try again.' });
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <section className={styles.panel} aria-labelledby="import-progress-title">
      <div className={styles.panelHead}>
        <div>
          <div className={styles.titleLine}>
            <h2 id="import-progress-title" className={styles.title}>
              {view.fileName}
            </h2>
            <ToneChip tone={IMPORT_STATUS_TONES[view.status]}>{IMPORT_STATUS_LABELS[view.status]}</ToneChip>
          </div>
          <p className={styles.lead}>
            {applicationSourceLabel(view.source)} · {view.startedByName ? `Started by ${view.startedByName}` : 'Started'}
            {view.startedAt ? ` · ${formatDateTime(view.startedAt)}` : ''}
            {view.finishedAt ? ` · finished ${formatDateTime(view.finishedAt)}` : ''}
          </p>
        </div>
        {view.status === 'importing' && (
          <Button variant="outline" size="sm" isPending={isCancelling} onClick={() => void cancel()}>
            Cancel import
          </Button>
        )}
        {/* RNK-01 §2: IMP-02's way on to the ranking (R5). */}
        {(view.status === 'done' || view.status === 'done_with_problems') && imported > 0 && (
          <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/jobs/$jobId/$tab', params: { jobId: view.jobId, tab: 'ranking' } })}>
            See the ranking
            <ArrowRight size={14} aria-hidden />
          </Button>
        )}
      </div>

      <p className={styles.summary} aria-live="polite">
        {view.summary}
      </p>

      <div className={styles.meters}>
        <Meter label="Rows" done={counts.processed} total={counts.rows} />
        <Meter label="CVs" done={cvsSettled} total={imported} />
      </div>
      {view.status === 'cancelled' && <p className={styles.muted}>Cancelled. The rows already imported stay; the rest weren't imported.</p>}

      <ProblemRows view={view} />
    </section>
  );
}

function Meter({ label, done, total }: { readonly label: string; readonly done: number; readonly total: number }): ReactNode {
  const value = percent(done, total);
  return (
    <div className={styles.meter}>
      <div className={styles.meterHead}>
        <span className={styles.meterLabel}>{label}</span>
        <span className={styles.meterCount}>
          {formatNumber(done)} of {formatNumber(total)}
        </span>
      </div>
      <div className={styles.meterTrack} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={value}>
        {/* The one computed size on this screen: how far along it is. */}
        <span className={styles.meterFill} style={{ inlineSize: `${value}%` }} />
      </div>
    </div>
  );
}

function ProblemRows({ view }: { readonly view: ImportView }): ReactNode {
  const [filter, setFilter] = useState<ImportRowFilter>('needs_you');
  const [page, setPage] = useState(1);
  const rows = useImportRows(view.id, filter, page, true);
  const pages = rows.data ? Math.max(1, Math.ceil(rows.data.total / rows.data.pageSize)) : 1;

  return (
    <div className={styles.block}>
      <div className={styles.problemsHead}>
        <h3 className={styles.subtitle}>Row by row</h3>
        <Segmented
          label="Which rows"
          value={filter}
          options={FILTER_OPTIONS}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
        />
      </div>
      {rows.isPending && <SkeletonLines count={4} />}
      {rows.data && rows.data.rows.length === 0 && <p className={styles.muted}>{filter === 'needs_you' ? 'Nothing needs you here.' : 'No rows to show.'}</p>}
      {rows.data && rows.data.rows.length > 0 && (
        <ul className={styles.rowList}>
          {rows.data.rows.map((row) => (
            <ProblemRow key={row.id} importId={view.id} row={row} />
          ))}
        </ul>
      )}
      {pages > 1 && (
        <div className={styles.pager}>
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <span className={styles.muted}>
            Page {page} of {pages}
          </span>
          <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * What Retry shows the moment it is pressed (#11): the link is being tried again (§L29), so the
 * row asks nothing of a person until the server says otherwise.
 */
export function retryingRow(row: ImportRowView): ImportRowView {
  return { ...row, cvStatus: 'queued', cvProblem: null, message: null, canRetry: false, canUpload: false };
}

/** Redesign Group 3: an "already applied" row opens that application; "already imported" stays text. */
function applicationLinkOf(row: ImportRowView): string | null {
  if (row.status === 'skipped' && row.skipReason !== 'already_applied') return null;
  return row.applicationId;
}

/**
 * One row: what happened in §L26's words, and Retry · Upload CV right beside it. Retry changes
 * the row at once and puts it back, with the reason, if the server says no; Upload CV shows its
 * progress on this row only (CLAUDE.md §5a rules 1 and 4). A fixed row is tinted green.
 */
export function ProblemRow({ importId, row }: { readonly importId: string; readonly row: ImportRowView }): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((toasts) => toasts.show);
  // The optimistic row, then the server's, held until the refetch lands (§5a rule 3).
  const [held, setHeld] = useState<ImportRowView | null>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const shown = held ?? row;
  const applicationId = applicationLinkOf(shown);

  const refresh = async (): Promise<void> => {
    await Promise.all([queryClient.invalidateQueries({ queryKey: importKey(importId) }), queryClient.invalidateQueries({ queryKey: [...IMPORTS_KEY, 'rows', importId] })]);
  };

  const retry = async (): Promise<void> => {
    const before = shown;
    setHeld(retryingRow(before));
    setIsFixed(true);
    try {
      const next = await retryImportRow(importId, before.id);
      setHeld(next);
      show({ tone: 'success', message: 'Trying the link again' });
      await refresh();
      setHeld(null);
    } catch (error) {
      setHeld(null);
      setIsFixed(false);
      show({ tone: 'danger', message: "Couldn't try that link again", detail: error instanceof Error ? error.message : 'Try again.' });
    }
  };

  const upload = async (file: File): Promise<void> => {
    setIsUploading(true);
    try {
      const next = await uploadImportRowCv(importId, shown.id, file);
      setHeld(next);
      setIsFixed(true);
      show({ tone: 'success', message: 'CV attached' });
      await refresh();
      setHeld(null);
    } catch (error) {
      show({ tone: 'danger', message: "Couldn't attach that CV", detail: error instanceof Error ? error.message : 'Try again.' });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <li className={styles.rowItem} data-fixed={isFixed || undefined}>
      {/* The spreadsheet's own row number: its row 1 is the column names. */}
      <span className={styles.rowNumber}>Row {formatNumber(shown.rowNumber + 1)}</span>
      <span className={styles.rowName}>
        {applicationId ? (
          <Link to="/applications/$applicationId" params={{ applicationId }} className={styles.rowLink}>
            {shown.name}
          </Link>
        ) : (
          shown.name
        )}
      </span>
      <span className={styles.rowMessage}>{isUploading ? 'Uploading the CV…' : rowMessageOf(shown)}</span>
      <span className={styles.rowActions}>
        {shown.cvDocumentId && shown.cvStatus === 'attached' && (
          <a className={styles.rowLink} href={documentUrl(shown.cvDocumentId)} target="_blank" rel="noreferrer">
            Open CV
          </a>
        )}
        {shown.canRetry && !isUploading && (
          <Button variant="ghost" size="sm" icon={RotateCw} onClick={() => void retry()}>
            Retry
          </Button>
        )}
        {shown.canUpload && <UploadCvButton name={shown.name} isPending={isUploading} onFile={(file) => void upload(file)} />}
      </span>
    </li>
  );
}

/** "Upload CV": a hidden file input behind our button, so the browser's own control never shows (CLAUDE.md §5). */
function UploadCvButton({ name, isPending, onFile }: { readonly name: string; readonly isPending: boolean; readonly onFile: (file: File) => void }): ReactNode {
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        className={styles.hiddenFile}
        accept={CV_ACCEPT}
        aria-label={`Upload a CV for ${name}`}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) onFile(file);
        }}
      />
      <Button variant="outline" size="sm" icon={Upload} isPending={isPending} onClick={() => inputRef.current?.click()}>
        Upload CV
      </Button>
    </>
  );
}

function rowMessageOf(row: ImportRowView): string {
  if (row.message) return row.message;
  if (row.cvStatus === 'queued') return 'Downloading the CV…';
  if (row.cvStatus === 'attached') return 'Imported, CV attached';
  if (row.status === 'pending') return 'Waiting to be imported';
  return 'Imported';
}
